'use client';

import { motion } from 'framer-motion';

interface OAuthButtonProps {
  provider: 'google' | 'microsoft';
  text: string;
  onClick?: () => void;
}

export default function OAuthButton({ provider, text, onClick }: OAuthButtonProps) {
  const getProviderIcon = () => {
    if (provider === 'google') {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    }
    // Microsoft icon (for future use)
    return (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.4 11.4H2V2h9.4v9.4z" fill="#f25022"/>
        <path d="M22 11.4h-9.4V2H22v9.4z" fill="#7fba00"/>
        <path d="M11.4 22H2v-9.4h9.4V22z" fill="#00a4ef"/>
        <path d="M22 22h-9.4v-9.4H22V22z" fill="#ffb900"/>
      </svg>
    );
  };

  /**
   * Get the OAuth redirect URL based on current environment.
   * - lowebi.com domains: Use relative URL (nginx proxies to backend)
   * - localhost: Use absolute URL to backend port 7710
   */
  const getOAuthUrl = (providerName: string): string => {
    if (typeof window === 'undefined') {
      return `/auth/${providerName}`;
    }

    const hostname = window.location.hostname;

    // Production/Test/Dev lowebi.com domains - use relative URL
    // Nginx reverse proxy routes /auth/* to backend
    if (hostname.includes('lowebi.com')) {
      return `/auth/${providerName}`;
    }

    // Local development - frontend on 7711, backend on 7710
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `http://localhost:7710/auth/${providerName}`;
    }

    // Fallback to relative URL
    return `/auth/${providerName}`;
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      window.location.href = getOAuthUrl(provider);
    }
  };

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 shadow-sm hover:shadow"
    >
      {getProviderIcon()}
      <span>{text}</span>
    </motion.button>
  );
}
